import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageSquare, Plus, Sparkles, MessageCircle, BookOpen, Users, Lock } from "lucide-react";
import { StewChat } from "@/components/stew/StewChat";
import { StewHistoryDrawer } from "@/components/stew/StewHistoryDrawer";
import SupportLibrary from "@/components/support/SupportLibrary";
import { AskAPersonForm } from "@/components/support/AskAPersonForm";

type TabKey = "stew" | "library" | "ask";

interface QuestionRow {
  id: string;
  question_text: string;
  status: "open" | "pending" | "answered" | "closed";
  route: "gathering" | "private";
  answer_text: string | null;
  answered_at: string | null;
  notified_at: string | null;
  created_at: string;
}

interface LegacyTicketRow {
  id: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
}

const STATUS_LABEL: Record<QuestionRow["status"], string> = {
  open: "Waiting for the congregation",
  pending: "Coming soon",
  answered: "Answered",
  closed: "Not taken up",
};

const ROUTE_LABEL: Record<QuestionRow["route"], string> = {
  gathering: "For the congregation",
  private: "Private",
};

export default function Support() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Path-param tab: /support, /support/stew, /support/library, /support/ask.
  // Legacy ?tab=stew|library|ask|faq|tickets on bare /support redirects once.
  const pathSeg = location.pathname.split("/")[2];
  const rawTab = searchParams.get("tab");

  useEffect(() => {
    if (pathSeg) return;
    if (!rawTab) return;
    const target: TabKey | null =
      rawTab === "ask" || rawTab === "tickets"
        ? "ask"
        : rawTab === "library" || rawTab === "faq"
        ? "library"
        : rawTab === "stew"
        ? "stew"
        : null;
    if (!target) return;
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    const search = next.toString();
    navigate(
      {
        pathname: `/support/${target}`,
        search: search ? `?${search}` : "",
        hash: location.hash,
      },
      { replace: true },
    );
  }, [pathSeg, rawTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const tab: TabKey =
    pathSeg === "ask" || pathSeg === "library" || pathSeg === "stew"
      ? (pathSeg as TabKey)
      : "stew";

  const setTab = (next: TabKey) => {
    const search = location.search;
    const hash = location.hash;
    navigate(
      {
        pathname: next === "stew" ? "/support" : `/support/${next}`,
        search,
        hash,
      },
      { replace: true },
    );
  };

  // Stew state
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);
  const [pendingAsk, setPendingAsk] = useState<string | null>(null);

  const askParam = searchParams.get("ask");
  useEffect(() => {
    if (!askParam) return;
    setActiveConversationId(null);
    setPendingAsk(askParam);
    const params = new URLSearchParams(searchParams);
    params.delete("ask");
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askParam]);

  // Merged history: new questions via RPC + legacy tickets read-only
  const { data: questions = [], refetch: refetchQuestions } = useQuery({
    queryKey: ["my-ask-questions", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("list_my_gathering_questions");
      if (error) throw error;
      return (data ?? []) as QuestionRow[];
    },
  });

  const { data: legacyTickets = [] } = useQuery({
    queryKey: ["my-legacy-tickets", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("id, subject, message, status, created_at")
        // Legacy tickets = ones NOT tied to a new-flow question (route=private
        // questions insert a fresh linked ticket, which we show as the question)
        .not(
          "id",
          "in",
          "(select ticket_id from congregation_questions where ticket_id is not null)"
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as LegacyTicketRow[];
    },
  });

  // Merge by created_at desc, tag each entry with its source
  type Entry =
    | { kind: "question"; created_at: string; row: QuestionRow }
    | { kind: "legacy"; created_at: string; row: LegacyTicketRow };
  const historyEntries = useMemo<Entry[]>(() => {
    const qEntries: Entry[] = questions.map((row) => ({
      kind: "question",
      created_at: row.created_at,
      row,
    }));
    const tEntries: Entry[] = legacyTickets.map((row) => ({
      kind: "legacy",
      created_at: row.created_at,
      row,
    }));
    return [...qEntries, ...tEntries].sort((a, b) =>
      b.created_at.localeCompare(a.created_at)
    );
  }, [questions, legacyTickets]);

  return (
    <MainLayout>
      <div className="container max-w-5xl mx-auto px-3 md:px-4 py-0 md:py-4 space-y-3 md:space-y-5">
        <header
          className={
            (tab === "stew" ? "hidden md:flex " : "flex ") +
            "flex-col md:flex-row md:items-end md:justify-between gap-3 pt-3 md:pt-0"
          }
        >
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight title-rule">
              How can we help?
            </h1>

            <p className="text-sm text-muted-foreground mt-1">
              Ask Stew for a fast answer, browse the library, or ask a person.
            </p>
          </div>
          {tab === "stew" && (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveConversationId(null)}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                New
              </Button>
              <StewHistoryDrawer
                activeId={activeConversationId}
                onSelect={setActiveConversationId}
                refreshToken={historyRefreshToken}
              />
            </div>
          )}
        </header>

        <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
          <TabsList
            className={
              "inline-flex w-full md:w-auto " +
              (tab === "stew"
                ? "sticky top-0 z-30 bg-background/95 backdrop-blur md:static md:bg-muted md:backdrop-blur-none"
                : "")
            }
          >
            <TabsTrigger value="stew" className="flex-1 md:flex-none gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              Ask Stew
            </TabsTrigger>
            <TabsTrigger value="library" className="flex-1 md:flex-none gap-1.5">
              <BookOpen className="h-3.5 w-3.5" />
              Library
            </TabsTrigger>
            <TabsTrigger value="ask" className="flex-1 md:flex-none gap-1.5">
              <MessageCircle className="h-3.5 w-3.5" />
              Ask a person
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stew" className="mt-2 md:mt-4 space-y-2 md:space-y-3">
            <StewChat
              conversationId={activeConversationId}
              onConversationCreated={(id) => {
                setActiveConversationId(id);
                setHistoryRefreshToken((n) => n + 1);
              }}
              onConversationTouched={() =>
                setHistoryRefreshToken((n) => n + 1)
              }
              initialMessage={pendingAsk}
              onInitialMessageConsumed={() => setPendingAsk(null)}
            />
            <p className="hidden md:block text-[11px] md:text-xs leading-snug text-muted-foreground text-center px-2">
              If Stew cannot help,{" "}
              <button
                type="button"
                onClick={() => setTab("ask")}
                className="underline hover:text-foreground"
              >
                ask a person
              </button>
              , or{" "}
              <a
                href="/dashboard#this-weeks-gathering"
                className="underline hover:text-foreground"
              >
                bring it to this week's congregation
              </a>
              .
            </p>
          </TabsContent>



          <TabsContent value="library" className="mt-4">
            <SupportLibrary onAskStew={() => setTab("stew")} />
          </TabsContent>

          <TabsContent value="ask" className="mt-4 space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold">Ask a person</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Bring it to this week's congregation, or ask a person privately.
                    You'll get the answer either way.
                  </p>
                </div>
                <AskAPersonForm
                  defaultRoute="gathering"
                  onSubmitted={() => refetchQuestions()}
                />
              </CardContent>
            </Card>

            <section className="space-y-3">
              <div className="flex items-baseline justify-between">
                <h3 className="text-sm font-semibold">What you've asked</h3>
                {questions.length > 0 && (
                  <Link
                    to="/support/questions"
                    className="text-xs text-muted-foreground underline hover:text-foreground"
                  >
                    Full history
                  </Link>
                )}
              </div>

              {historyEntries.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-10">
                    <MessageSquare className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Nothing yet. Ask above and it will appear here.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <ul className="space-y-2">
                  {historyEntries.map((entry) =>
                    entry.kind === "question" ? (
                      <li key={`q-${entry.row.id}`}>
                        <Link
                          to={`/support/questions/${entry.row.id}`}
                          className="block rounded-lg border bg-card hover:bg-muted/40 transition-colors p-3"
                        >
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <p className="text-sm flex-1 min-w-0 line-clamp-2">
                              {entry.row.question_text}
                            </p>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <Badge variant="outline" className="text-[10px]">
                                {entry.row.route === "private" ? (
                                  <>
                                    <Lock className="w-3 h-3 mr-1" />
                                    {ROUTE_LABEL.private}
                                  </>
                                ) : (
                                  <>
                                    <Users className="w-3 h-3 mr-1" />
                                    {ROUTE_LABEL.gathering}
                                  </>
                                )}
                              </Badge>
                              <Badge
                                variant={
                                  entry.row.status === "answered"
                                    ? "default"
                                    : entry.row.status === "closed"
                                    ? "outline"
                                    : "secondary"
                                }
                              >
                                {STATUS_LABEL[entry.row.status]}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Asked{" "}
                            {formatDistanceToNow(new Date(entry.row.created_at), {
                              addSuffix: true,
                            })}
                          </p>
                        </Link>
                      </li>
                    ) : (
                      <li key={`t-${entry.row.id}`}>
                        <div className="block rounded-lg border bg-muted/20 p-3">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <p className="text-sm flex-1 min-w-0 line-clamp-2">
                              {entry.row.subject}
                            </p>
                            <Badge variant="outline">
                              {entry.row.status.replace("_", " ")}
                            </Badge>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Asked{" "}
                            {formatDistanceToNow(new Date(entry.row.created_at), {
                              addSuffix: true,
                            })}{" "}
                            · Earlier request
                          </p>
                        </div>
                      </li>
                    )
                  )}
                </ul>
              )}
            </section>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
